import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Alert,
  Tooltip,
  CircularProgress,
  Snackbar,
  InputAdornment,
  DialogContentText
} from '@mui/material';
import {
  Download as DownloadIcon,
  Visibility as VisibilityIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  CloudDownload as CloudDownloadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Schedule as ScheduleIcon,
  Edit as EditIcon,
  Code as CodeIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import { pesquisaApi } from '../../services/apiPesquisa.servise';
import { dataProcessingService } from '../../services/dataProcessing.service';
import LateralMenu from '../../components/LateralMenu/LateralMenu';
import { useNavigate } from 'react-router-dom';

const ProcessList = () => {
  const navigate = useNavigate();
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProcess, setSelectedProcess] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState({});
  const [codingLoading, setCodingLoading] = useState({});
  
  // Estados para edição
  const [editingTaskName, setEditingTaskName] = useState(false);
  const [editedTaskName, setEditedTaskName] = useState('');
  const [savingTaskName, setSavingTaskName] = useState(false);
  
  // Estados para exclusão
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [processToDelete, setProcessToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Paginação
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  // Filtros
  const [searchText, setSearchText] = useState('');
  const [taskIdFilter, setTaskIdFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [orderBy, setOrderBy] = useState('created_at');
  const [order, setOrder] = useState('desc');

  // Notificações
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Função para formatar data
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.error('Erro ao formatar data:', error);
      return dateString;
    }
  };

  // Carregar processos com filtros
  const loadProcesses = async (pageNum = 0, search = '', filters = {}) => {
    setLoading(true);
    try {
      const filtersToApply = {
        ...filters,
        ordering: order === 'desc' ? `-${orderBy}` : orderBy
      };
      
      const result = await pesquisaApi.getProcessedTasks(
        pageNum + 1,
        rowsPerPage,
        search,
        filtersToApply
      );
      
      setProcesses(result.tasks || []);
      setTotalCount(result.pagination?.count || 0);
      
      console.log(`📊 ${result.tasks.length} processos carregados, total: ${result.pagination?.count}`);
    } catch (error) {
      console.error('Erro ao carregar processos:', error);
      setProcesses([]);
      setTotalCount(0);
      setSnackbar({
        open: true,
        message: 'Erro ao carregar processos',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  // Carregar inicialmente
  useEffect(() => {
    const filters = {};
    if (taskIdFilter) filters.taskId = taskIdFilter;
    loadProcesses(page, searchText, filters);
  }, [page, rowsPerPage, orderBy, order]);

  // Buscar com filtros
  const handleSearch = () => {
    setPage(0);
    const filters = {};
    if (taskIdFilter) filters.taskId = taskIdFilter;
    loadProcesses(0, searchText, filters);
  };

  // Limpar filtros
  const handleClearFilters = () => {
    setSearchText('');
    setTaskIdFilter('');
    setPage(0);
    loadProcesses(0, '', {});
  };

  const handleStartCoding = async (process) => {
    const key = `coding_${process.task_id}`;
    setCodingLoading(prev => ({ ...prev, [key]: true }));
    
    try {
      console.log(`🚀 Iniciando codificação para processo #${process.task_id}`);
      
      // 1. Baixar a planilha de referência
      const response = await dataProcessingService.downloadResult('planilha_ref', process.task_id);
      
      if (!response.data || response.data.size < 100) {
        throw new Error('Planilha de referência inválida ou vazia');
      }
      
      // 2. Extrair o nome da via do nome do arquivo ou do task_name
      let roadName = process.task_name || '';
      if (!roadName && process.axis_file_name) {
        // Tentar extrair do nome do arquivo (ex: "BR-101_Eixo.gpkg" -> "BR-101")
        const fileName = process.axis_file_name.split('_').slice(0, -1).join('_') + ".gpkg";
        roadName = fileName.replace('.gpkg', '').replace('_', ' ');
      }
      
      // 3. Preparar dados para enviar ao Form2
      const formData = {
        taskId: process.task_id,
        roadName: roadName,
        arquivo: new File([response.data], `planilha_ref_${process.task_id}.xlsx`, {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        })
      };
      
      console.log('📤 Dados para codificação:', formData);
      
      // 4. Navegar para o Form2 com os dados
      navigate('/forms', { 
        state: formData 
      });
      
    } catch (error) {
      console.error('Erro ao iniciar codificação:', error);
      setSnackbar({
        open: true,
        message: `Erro ao iniciar codificação: ${error.message}`,
        severity: 'error'
      });
    } finally {
      setCodingLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  // Download de arquivos
  const downloadFile = async (processId, fileType, fileName) => {
    const key = `${processId}_${fileType}`;
    setDownloadLoading(prev => ({ ...prev, [key]: true }));
    
    try {
      const response = await dataProcessingService.downloadResult(fileType, processId);
      
      // Criar link para download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setSnackbar({
        open: true,
        message: 'Download iniciado',
        severity: 'success'
      });
    } catch (error) {
      console.error('Erro no download:', error);
      setSnackbar({
        open: true,
        message: 'Erro ao baixar arquivo',
        severity: 'error'
      });
    } finally {
      setDownloadLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  // Baixar todos os arquivos de um processo
  const downloadAllFiles = async (process) => {
    const files = [
      { type: 'estaqueamento_1km', name: `estaqueamento_1km_${process.task_id}.gpkg` },
      { type: 'estaqueamento_20m', name: `estaqueamento_20m_${process.task_id}.gpkg` },
      { type: 'planilha_ref', name: `planilha_ref_${process.task_id}.xlsx` }
    ];

    for (const file of files) {
      await downloadFile(process.task_id, file.type, file.name);
      await new Promise(resolve => setTimeout(resolve, 300));
    }
  };

  // Editar nome da tarefa
  const handleEditTaskName = () => {
    setEditingTaskName(true);
    setEditedTaskName(selectedProcess?.task_name || '');
  };

  const handleCancelEdit = () => {
    setEditingTaskName(false);
    setEditedTaskName('');
  };

const handleSaveTaskName = async () => {
  if (!selectedProcess || !editedTaskName.trim()) {
    setSnackbar({
      open: true,
      message: 'O nome da tarefa não pode estar vazio',
      severity: 'warning'
    });
    return;
  }

  setSavingTaskName(true);
  try {
    // Chamada REAL para API
    const response = await pesquisaApi.updateTaskName(selectedProcess.task_id, editedTaskName);
    
    // Atualizar localmente
    const updatedProcesses = processes.map(process => 
      process.task_id === selectedProcess.task_id 
        ? { ...process, task_name: editedTaskName }
        : process
    );
    
    setProcesses(updatedProcesses);
    setSelectedProcess({ ...selectedProcess, task_name: editedTaskName });
    
    setEditingTaskName(false);
    setSnackbar({
      open: true,
      message: response.message || 'Nome da tarefa atualizado com sucesso!',
      severity: 'success'
    });
    
  } catch (error) {
    console.error('Erro ao atualizar nome da tarefa:', error);
    setSnackbar({
      open: true,
      message: error.message || 'Erro ao atualizar nome da tarefa',
      severity: 'error'
    });
  } finally {
    setSavingTaskName(false);
  }
};


  // Excluir processamento
  const handleDeleteClick = (process) => {
    setProcessToDelete(process);
    setDeleteDialogOpen(true);
  };

const handleConfirmDelete = async () => {
  if (!processToDelete) return;
  
  setDeleting(true);
  try {
    // Chamada REAL para API
    const response = await pesquisaApi.deleteTask(processToDelete.task_id);
    
    // Atualizar localmente
    const updatedProcesses = processes.filter(process => 
      process.task_id !== processToDelete.task_id
    );
    
    setProcesses(updatedProcesses);
    setTotalCount(prev => prev - 1);
    
    if (selectedProcess?.task_id === processToDelete.task_id) {
      setDialogOpen(false);
      setSelectedProcess(null);
    }
    
    setSnackbar({
      open: true,
      message: response.message || 'Processamento excluído com sucesso!',
      severity: 'success'
    });
    
  } catch (error) {
    console.error('Erro ao excluir processamento:', error);
    setSnackbar({
      open: true,
      message: error.message || 'Erro ao excluir processamento',
      severity: 'error'
    });
  } finally {
    setDeleting(false);
    setDeleteDialogOpen(false);
    setProcessToDelete(null);
  }
};

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setProcessToDelete(null);
  };

  // Status chip
  const getStatusChip = (status) => {
    const statusConfig = {
      COMPLETED: { label: 'Concluído', color: 'success', icon: <CheckCircleIcon /> },
      PROCESSING: { label: 'Processando', color: 'warning', icon: <ScheduleIcon /> },
      FAILED: { label: 'Falhou', color: 'error', icon: <ErrorIcon /> },
      PENDING: { label: 'Pendente', color: 'default', icon: <ScheduleIcon /> }
    };

    const config = statusConfig[status] || { label: status, color: 'default' };
    
    return (
      <Chip
        icon={config.icon}
        label={config.label}
        color={config.color}
        size="small"
        variant="outlined"
      />
    );
  };

  // Ordenação
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Colunas da tabela
  const columns = [
    { id: 'task_id', label: 'ID', sortable: true },
    { id: 'task_name', label: 'Nome da Via', sortable: true },
    { id: 'axis_file_name', label: 'Arquivo do Eixo', sortable: false },
    { id: 'status', label: 'Status', sortable: false },
    { id: 'created_at', label: 'Data de Criação', sortable: true },
    { id: 'actions', label: 'Ações', sortable: false }
  ];

  // Renderizar tabela
  const renderTable = () => (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell key={column.id}>
                {column.sortable ? (
                  <Button
                    onClick={() => handleRequestSort(column.id)}
                    size="small"
                    sx={{ textTransform: 'none', fontWeight: 'bold' }}
                  >
                    {column.label}
                    {orderBy === column.id && (
                      <span style={{ marginLeft: 4 }}>
                        {order === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </Button>
                ) : (
                  <Typography variant="subtitle2" fontWeight="bold">
                    {column.label}
                  </Typography>
                )}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <CircularProgress size={24} />
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Carregando processos...
                </Typography>
              </TableCell>
            </TableRow>
          ) : processes.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} align="center">
                <Typography variant="body2" color="text.secondary">
                  Nenhum processo encontrado
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            processes.map((process) => (
              <TableRow 
                key={process.task_id}
                hover
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight="bold">
                    #{process.task_id}
                  </Typography>
                </TableCell>
                
                <TableCell>
                  <Typography variant="body2">
                    {process.task_name || '--'}
                  </Typography>
                </TableCell>
                
                <TableCell>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {process.axis_file_name.split('_').slice(0, -1).join('_') + ".gpkg" || 'N/A'}
                  </Typography>
                </TableCell>
                
                <TableCell>
                  {getStatusChip(process.status)}
                </TableCell>
                
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(process.created_at)}
                  </Typography>
                </TableCell>
                
                <TableCell align="center">
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                    {/* Botão de visualizar detalhes */}
                    <Tooltip title="Visualizar detalhes">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setSelectedProcess(process);
                          setDialogOpen(true);
                        }}
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    
                    {/* Botão de download */}
                    {process.status === 'COMPLETED' && (
                      <Tooltip title="Baixar todos os arquivos">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => downloadAllFiles(process)}
                          disabled={downloadLoading[`${process.task_id}_all`]}
                        >
                          {downloadLoading[`${process.task_id}_all`] ? (
                            <CircularProgress size={20} />
                          ) : (
                            <CloudDownloadIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Tooltip>
                    )}
                    
                    {/* Botão de exclusão */}
                    <Tooltip title="Excluir processamento">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteClick(process)}
                        disabled={deleting}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // Modal de detalhes com edição
  const renderDetailsDialog = () => (
    <Dialog
      open={dialogOpen}
      onClose={() => {
        setDialogOpen(false);
        setEditingTaskName(false);
        setEditedTaskName('');
      }}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Detalhes do Processo #{selectedProcess?.task_id}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {!editingTaskName ? (
              <Tooltip title="Editar nome da tarefa">
                <IconButton size="small" onClick={handleEditTaskName}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            ) : (
              <>
                <Tooltip title="Salvar alterações">
                  <span>
                    <IconButton 
                      size="small" 
                      color="primary" 
                      onClick={handleSaveTaskName}
                      disabled={savingTaskName}
                    >
                      {savingTaskName ? (
                        <CircularProgress size={20} />
                      ) : (
                        <SaveIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Cancelar edição">
                  <IconButton size="small" onClick={handleCancelEdit}>
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        {selectedProcess && (
          <Grid container spacing={3}>
            {/* Informações básicas */}
            <Grid item xs={12}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Informações do Processo
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        Nome da Via:
                      </Typography>
                      {editingTaskName ? (
                        <TextField
                          variant="outlined"
                          size="small"
                          value={editedTaskName}
                          onChange={(e) => setEditedTaskName(e.target.value)}
                          disabled={savingTaskName}
                          autoFocus
                          placeholder="Digite o novo nome da tarefa"
                          inputProps={{ maxLength: 255 }}
                        />
                      ) : (
                        <Typography variant="body1" sx={{ minHeight: '40px', display: 'flex', alignItems: 'center' }}>
                          {selectedProcess.task_name || 'Não especificado'}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Nome do Eixo:
                      </Typography>
                      <Typography variant="body1">
                        {selectedProcess.axis_file_name.split('_').slice(0, -1).join('_') + ".gpkg" || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Status:
                      </Typography>
                      {getStatusChip(selectedProcess.status)}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Direção do Eixo:
                      </Typography>
                      <Typography variant="body1">
                        {selectedProcess.eixo_direction || 'AUTOMÁTICO'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Criado em:
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(selectedProcess.created_at)}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2" color="text.secondary">
                        Atualizado em:
                      </Typography>
                      <Typography variant="body1">
                        {formatDate(selectedProcess.updated_at)}
                      </Typography>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            </Grid>

            {/* Arquivos disponíveis - apenas para processos COMPLETED */}
            {selectedProcess.status === 'COMPLETED' && (
              <Grid item xs={12}>
                <Card variant="outlined">
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Arquivos Gerados
                    </Typography>
                    <Grid container spacing={2}>
                      {[
                        { 
                          label: 'Estaqueamento 1km', 
                          type: 'estaqueamento_1km',
                          name: `estaqueamento_1km_${selectedProcess.task_id}.gpkg`,
                          desc: 'Pontos a cada 1km (arquivo GPKG)'
                        },
                        { 
                          label: 'Estaqueamento 20m', 
                          type: 'estaqueamento_20m',
                          name: `estaqueamento_20m_${selectedProcess.task_id}.gpkg`,
                          desc: 'Pontos a cada 20m (arquivo GPKG)'
                        },
                        { 
                          label: 'Planilha de Referência', 
                          type: 'planilha_ref',
                          name: `planilha_ref_${selectedProcess.task_id}.xlsx`,
                          desc: 'Planilha Excel com referências de imagens'
                        }
                      ].map((file) => (
                        <Grid item xs={12} md={4} key={file.type}>
                          <Paper sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <Typography variant="subtitle1" gutterBottom>
                              {file.label}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph sx={{ flexGrow: 1 }}>
                              {file.desc}
                            </Typography>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<DownloadIcon />}
                              fullWidth
                              onClick={() => downloadFile(
                                selectedProcess.task_id, 
                                file.type, 
                                file.name
                              )}
                              disabled={downloadLoading[`${selectedProcess.task_id}_${file.type}`]}
                            >
                              {downloadLoading[`${selectedProcess.task_id}_${file.type}`] ? (
                                <CircularProgress size={20} />
                              ) : (
                                'Baixar'
                              )}
                            </Button>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Botão para baixar tudo */}
            {selectedProcess.status === 'COMPLETED' && (
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  startIcon={<CloudDownloadIcon />}
                  onClick={() => downloadAllFiles(selectedProcess)}
                  fullWidth
                  disabled={downloadLoading[`${selectedProcess.task_id}_all`]}
                  size="large"
                >
                  {downloadLoading[`${selectedProcess.task_id}_all`] ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Baixando...
                    </>
                  ) : (
                    'Baixar Todos os Arquivos'
                  )}
                </Button>
              </Grid>
            )}

            {/* Botão para iniciar codificação */}
            {selectedProcess.status === 'COMPLETED' && (
              <Grid item xs={12}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<CodeIcon />}
                  onClick={() => handleStartCoding(selectedProcess)}
                  fullWidth
                  disabled={codingLoading[`coding_${selectedProcess.task_id}`]}
                  size="large"
                >
                  {codingLoading[`coding_${selectedProcess.task_id}`] ? (
                    <>
                      <CircularProgress size={20} sx={{ mr: 1 }} />
                      Preparando...
                    </>
                  ) : (
                    'Ir para Codificação'
                  )}
                </Button>
              </Grid>
            )}

            {/* Informações de erro para processos falhados */}
            {selectedProcess.status === 'FAILED' && selectedProcess.error_message && (
              <Grid item xs={12}>
                <Alert severity="error">
                  <Typography variant="subtitle2" gutterBottom>
                    Erro no processamento:
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedProcess.error_message}
                  </Typography>
                </Alert>
              </Grid>
            )}
          </Grid>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={() => {
          setDialogOpen(false);
          setEditingTaskName(false);
          setEditedTaskName('');
        }}>
          Fechar
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Modal de confirmação de exclusão
  const renderDeleteDialog = () => (
    <Dialog
      open={deleteDialogOpen}
      onClose={handleCancelDelete}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle color="error">
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ErrorIcon color="error" />
          Confirmar Exclusão
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <DialogContentText>
          <Typography variant="body1" gutterBottom>
            Tem certeza que deseja excluir o processamento <strong>#{processToDelete?.task_id}</strong>?
          </Typography>
          
          {processToDelete?.task_name && (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Nome: {processToDelete.task_name}
            </Typography>
          )}
          
          <Typography variant="body2" color="error" gutterBottom>
            ⚠️ Esta ação é irreversível e excluirá todos os arquivos associados a este processamento.
          </Typography>
          
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Atenção:</strong> Esta exclusão removerá permanentemente:
              <ul>
                <li>Estaqueamento 1km</li>
                <li>Estaqueamento 20m</li>
                <li>Planilha de referência</li>
                <li>Todos os metadados do processamento</li>
              </ul>
            </Typography>
          </Alert>
        </DialogContentText>
      </DialogContent>
      
      <DialogActions>
        <Button 
          onClick={handleCancelDelete}
          disabled={deleting}
        >
          Cancelar
        </Button>
        <Button 
          onClick={handleConfirmDelete}
          color="error"
          variant="contained"
          startIcon={deleting ? <CircularProgress size={20} /> : <DeleteIcon />}
          disabled={deleting}
        >
          {deleting ? 'Excluindo...' : 'Excluir Permanentemente'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Filtros
  const renderFilters = () => (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                <TextField
          placeholder="Buscar por ID, nome ou arquivo..."
          variant="outlined"
          size="small"
          sx={{ flexGrow: 1, minWidth: 200 }}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        
        {/* Botão de filtros (mobile) */}
        <Tooltip title="Filtros">
          <IconButton
            variant="outlined"
            onClick={() => setShowFilters(!showFilters)}
            sx={{
              display: { xs: 'flex', sm: 'none' },
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <FilterListIcon />
          </IconButton>
        </Tooltip>
        
        {/* Botão de filtros (desktop) */}
        <Button
          variant="outlined"
          startIcon={<FilterListIcon />}
          onClick={() => setShowFilters(!showFilters)}
          sx={{
            display: { xs: 'none', sm: 'flex' }
          }}
        >
          Filtros
        </Button>

        {/* Botão de busca (mobile) */}
        <Tooltip title="Buscar">
          <IconButton
            variant="contained"
            onClick={handleSearch}
            color="primary"
            sx={{
              display: { xs: 'flex', sm: 'none' }
            }}
          >
            <SearchIcon />
          </IconButton>
        </Tooltip>
        
        {/* Botão de busca (desktop) */}
        <Button
          variant="contained"
          onClick={handleSearch}
          startIcon={<SearchIcon />}
          sx={{
            display: { xs: 'none', sm: 'flex' }
          }}
        >
          Buscar
        </Button>

        {/* Botão de limpar (mobile) */}
        <Tooltip title="Limpar filtros">
          <IconButton
            variant="outlined"
            onClick={handleClearFilters}
            color="secondary"
            sx={{
              display: { xs: 'flex', sm: 'none' },
              border: '1px solid',
              borderColor: 'divider'
            }}
          >
            <ClearIcon />
          </IconButton>
        </Tooltip>
        
        {/* Botão de limpar (desktop) */}
        <Button
          variant="outlined"
          onClick={handleClearFilters}
          startIcon={<ClearIcon />}
          sx={{
            display: { xs: 'none', sm: 'flex' }
          }}
        >
          Limpar
        </Button>
      </Box>

      {/* Filtros avançados (expansível) */}
      {showFilters && (
        <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', mt: 2 }}>
          <TextField
            label="ID da Tarefa"
            variant="outlined"
            size="small"
            value={taskIdFilter}
            onChange={(e) => setTaskIdFilter(e.target.value)}
            sx={{ minWidth: 150 }}
            placeholder="Ex: 123"
          />
          
          {/* Chip de ordenação */}
          {/* <Chip 
            label={`Ordenação: ${order === 'desc' ? 'Mais recentes' : 'Mais antigas'}`}
            onDelete={() => handleRequestSort('created_at')}
            variant="outlined"
          /> */}
        </Box>
      )}
    </Paper>
  );

  return (
    <>
      <LateralMenu />
      <Box sx={{
          width: "100%",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          bgcolor: "#e7eaef",

      }}>
      <Container  maxWidth="lg" sx={{ p: { xs: 1, sm: 2, md: 3 }, mt: 4, mb: 4, mt: { xs: 6, sm: 8 }  }}>
        {/* Cabeçalho */}
        <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
          <Typography variant="h4" gutterBottom color="primary" align="center" >
            Processos Gerados
          </Typography>
          <Typography variant="body1" color="text.secondary" align="center" sx={{ mb: 4 }}>
            Visualize, edite e exclua os resultados dos processamentos realizados
          </Typography>
        </Box>

        {/* Filtros */}
        {renderFilters()}

        {/* Tabela de processos */}
          {loading && processes.length === 0 ? (
            <Box sx={{ width: '100%', mb: 2 }}>
              <LinearProgress />
              <Typography variant="body2" align="center" sx={{ mt: 1 }}>
                Carregando processos...
              </Typography>
            </Box>
          ) : (
            <>
              {renderTable()}
              
              {/* Paginação */}
              <TablePagination
                rowsPerPageOptions={[5, 10, 25, 50]}
                component="div"
                count={totalCount}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={(e, newPage) => setPage(newPage)}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                labelRowsPerPage="Processos por página:"
                labelDisplayedRows={({ from, to, count }) => 
                  `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
                }
              />
            </>
          )}
        </Paper>
      </Container>

      {/* Modal de detalhes com edição */}
      {renderDetailsDialog()}
      
      {renderDeleteDialog()}

      {/* Snackbar de notificação */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      </Box>
    </>
  );
};

export default ProcessList;